import React, { useMemo, useState } from "react";
import { Plus, Trash2, BookOpen } from "lucide-react";
import LibraryPicker from "@/components/LibraryPicker";

/**
 * SectionTable — multi-row editor for multi-section calculators.
 *
 * Two modes (selected via `mode` prop, default "annular"):
 *
 * 1) mode="annular"  → Multi-Section Annular Volume
 *    Emits 6-token CSV that `multiSectionAnnularVolume()` parses:
 *      "name,boundaryType,outerID,tubularType,tubularOD,length"
 *    Per-row volume: (outerID² − tubularOD²) × L / 1029.4
 *    Library kinds:
 *      • Tubular picker → ["drill_pipe","hwdp","drill_collar"] (default)
 *      • Outer picker  → ["casing"] when boundaryType="casing",
 *                        ["bit"]    when boundaryType="openhole"
 *
 * 2) mode="drillstring" → Multi-Section Drill String Internal Volume
 *    Emits 3-token CSV that `multiSectionDrillStringVolume()` parses:
 *      "componentType,id,length"
 *    Per-row volume: ID² × L / 1029.4
 *    Library kinds:
 *      • Tubular picker → ["drill_pipe","hwdp","drill_collar","tubing"]
 *        (picker emits item.id_in into the ID field, plus libraryKind label
 *        into componentType)
 *    No boundary toggle; no outer-ID column.
 *
 * Back-compat: on mount the incoming `value` (legacy CSV blob from a saved
 * sheet) is parsed and hydrated into the table state.
 *
 * Spec via `spec` prop:
 *   { mode, tubularKinds?, outerKindsCasing?, outerKindsOpenHole? }
 *   `mode` here takes precedence over the `mode` prop if provided.
 */

const FORMULA_PER_BBL = 1029.4;
const MIN_TOUCH = 44; // px — mobile touch-target minimum

const DEFAULT_TUBULAR_KINDS_ANNULAR    = ["drill_pipe", "hwdp", "drill_collar"];
const DEFAULT_TUBULAR_KINDS_DRILLSTRING = ["drill_pipe", "hwdp", "drill_collar", "tubing"];
const DEFAULT_OUTER_KINDS_CASING       = ["casing"];
const DEFAULT_OUTER_KINDS_OPENHOLE     = ["bit"];

const _num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const emptyAnnularRow = (i) => ({
  id: `r${Date.now()}_${i}_${Math.random().toString(36).slice(2, 7)}`,
  name: `Section ${i + 1}`,
  boundaryType: "casing", // "casing" | "openhole"
  outerId: "",
  outerFromLib: false,
  tubularType: "",
  tubularOd: "",
  tubularFromLib: false,
  length: "",
});

const emptyDrillstringRow = (i) => ({
  id: `r${Date.now()}_${i}_${Math.random().toString(36).slice(2, 7)}`,
  componentType: "",
  componentId: "", // tubular ID (in)
  componentFromLib: false,
  length: "",
});

function parseCsvToRowsAnnular(csv) {
  if (!csv || typeof csv !== "string") return [];
  const lines = csv.split(/\r?\n/).map((l) => l.trim()).filter((l) => l && !l.startsWith("#"));
  return lines.map((line, idx) => {
    const t = line.split(",").map((x) => x.trim());
    const base = emptyAnnularRow(idx);
    if (t.length >= 6) {
      base.name = t[0] || base.name;
      base.boundaryType = /open/i.test(t[1] || "") ? "openhole" : "casing";
      base.outerId = t[2] || "";
      base.tubularType = t[3] || "";
      base.tubularOd = t[4] || "";
      base.length = t[5] || "";
    } else if (t.length === 3) {
      base.outerId = t[0] || "";
      base.tubularOd = t[1] || "";
      base.length = t[2] || "";
    }
    return base;
  });
}

function parseCsvToRowsDrillstring(csv) {
  if (!csv || typeof csv !== "string") return [];
  const lines = csv.split(/\r?\n/).map((l) => l.trim()).filter((l) => l && !l.startsWith("#"));
  return lines.map((line, idx) => {
    const t = line.split(",").map((x) => x.trim());
    const base = emptyDrillstringRow(idx);
    // Drill-string parser in lib/calc/drilling.js accepts:
    //   3 tokens: "componentType, ID, Length"
    //   2 tokens: "ID, Length"
    if (t.length >= 3 && _num(t[1]) !== null && _num(t[2]) !== null) {
      base.componentType = t[0] || "";
      base.componentId = t[1] || "";
      base.length = t[2] || "";
    } else if (t.length === 2) {
      base.componentId = t[0] || "";
      base.length = t[1] || "";
    }
    return base;
  });
}

function rowsToCsvAnnular(rows) {
  return rows
    .map((r, i) => {
      const name = r.name || `Section ${i + 1}`;
      const bt = r.boundaryType === "openhole" ? "openhole" : "casing";
      const tt = r.tubularType || "—";
      return `${name},${bt},${r.outerId || ""},${tt},${r.tubularOd || ""},${r.length || ""}`;
    })
    .join("\n");
}

function rowsToCsvDrillstring(rows) {
  return rows
    .map((r, i) => {
      const ct = r.componentType || `Component ${i + 1}`;
      return `${ct},${r.componentId || ""},${r.length || ""}`;
    })
    .join("\n");
}

function perRowVolumeAnnular(r) {
  const a = _num(r.outerId);
  const b = _num(r.tubularOd);
  const L = _num(r.length);
  if (a === null || b === null || L === null) return null;
  if (a <= 0 || b <= 0 || L <= 0) return null;
  if (b >= a) return null;
  return ((a * a) - (b * b)) * L / FORMULA_PER_BBL;
}

function perRowVolumeDrillstring(r) {
  const id = _num(r.componentId);
  const L = _num(r.length);
  if (id === null || L === null) return null;
  if (id <= 0 || L <= 0) return null;
  return (id * id) * L / FORMULA_PER_BBL;
}

export default function SectionTable({ value, onChange, mode = "annular", spec = null }) {
  // `spec.mode` (from catalog sectionTableSpec) takes precedence over the
  // top-level `mode` prop so the catalog stays the single source of truth.
  const activeMode = (spec && spec.mode) || mode || "annular";
  const isDrillstring = activeMode === "drillstring";

  const tubularKinds = useMemo(() => {
    if (spec && Array.isArray(spec.tubularKinds) && spec.tubularKinds.length > 0) return spec.tubularKinds;
    return isDrillstring ? DEFAULT_TUBULAR_KINDS_DRILLSTRING : DEFAULT_TUBULAR_KINDS_ANNULAR;
  }, [spec, isDrillstring]);

  const outerKindsCasing = useMemo(() => {
    if (spec && Array.isArray(spec.outerKindsCasing) && spec.outerKindsCasing.length > 0) return spec.outerKindsCasing;
    return DEFAULT_OUTER_KINDS_CASING;
  }, [spec]);

  const outerKindsOpenHole = useMemo(() => {
    if (spec && Array.isArray(spec.outerKindsOpenHole) && spec.outerKindsOpenHole.length > 0) return spec.outerKindsOpenHole;
    return DEFAULT_OUTER_KINDS_OPENHOLE;
  }, [spec]);

  const parseCsvToRows = isDrillstring ? parseCsvToRowsDrillstring : parseCsvToRowsAnnular;
  const rowsToCsv = isDrillstring ? rowsToCsvDrillstring : rowsToCsvAnnular;
  const emptyRow = isDrillstring ? emptyDrillstringRow : emptyAnnularRow;
  const perRowVolume = isDrillstring ? perRowVolumeDrillstring : perRowVolumeAnnular;

  const [rows, setRows] = useState(() => {
    const hydrated = parseCsvToRows(value);
    return hydrated.length ? hydrated : [emptyRow(0)];
  });
  const [pickerFor, setPickerFor] = useState(null); // { rowId, kind: "tubular"|"outer" }
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => { onChangeRef.current = onChange; });

  // Emit CSV upward immediately after every mutation (no effect, no loop).
  const commit = (next) => {
    setRows(next);
    if (onChangeRef.current) onChangeRef.current(rowsToCsv(next));
  };

  // Re-hydrate ONLY when the parent value's content has actually diverged
  // from what we last emitted (back-compat for Reset / external sheet load).
  const lastEmittedRef = React.useRef(rowsToCsv(rows));
  React.useEffect(() => {
    const incoming = (value || "").trim();
    if (!incoming) return;
    if (incoming === lastEmittedRef.current.trim()) return;
    const next = parseCsvToRows(incoming);
    if (!next.length) return;
    setRows(next);
    lastEmittedRef.current = rowsToCsv(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const updateRow = (rowId, patch) => {
    const next = rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r));
    lastEmittedRef.current = rowsToCsv(next);
    commit(next);
  };
  const addRow = () => {
    const next = [...rows, emptyRow(rows.length)];
    lastEmittedRef.current = rowsToCsv(next);
    commit(next);
  };
  const removeRow = (rowId) => {
    if (rows.length <= 1) return;
    const next = rows
      .filter((r) => r.id !== rowId)
      .map((r, i) => {
        if (isDrillstring) return r; // no auto-rename in drillstring mode
        return { ...r, name: r.name?.startsWith("Section ") ? `Section ${i + 1}` : r.name };
      });
    lastEmittedRef.current = rowsToCsv(next);
    commit(next);
  };

  const openPicker = (rowId, kind) => setPickerFor({ rowId, kind });

  const pickerKinds = useMemo(() => {
    if (!pickerFor) return [];
    if (pickerFor.kind === "tubular") return tubularKinds;
    const r = rows.find((x) => x.id === pickerFor.rowId);
    return r && r.boundaryType === "openhole" ? outerKindsOpenHole : outerKindsCasing;
  }, [pickerFor, rows, tubularKinds, outerKindsCasing, outerKindsOpenHole]);

  const handlePick = (item, libraryKind) => {
    if (!pickerFor) return;
    let patch;
    if (isDrillstring && pickerFor.kind === "tubular") {
      // Drillstring picker emits id_in into the row's ID, plus the libraryKind
      // label into componentType so the saved CSV carries an audit-friendly
      // component name (e.g. "drill_pipe").
      patch = {
        componentType: libraryKind,
        componentId: String(item.id_in ?? ""),
        componentFromLib: true,
      };
    } else if (pickerFor.kind === "tubular") {
      // Annular tubular picker — emits od_in into tubularOd.
      patch = {
        tubularType: libraryKind,
        tubularOd: String(item.od_in ?? ""),
        tubularFromLib: true,
      };
    } else {
      // Annular outer picker — bit → diameter_in, casing → id_in.
      const outer = libraryKind === "bit" ? item.diameter_in : item.id_in;
      patch = {
        outerId: String(outer ?? ""),
        outerFromLib: true,
      };
    }
    updateRow(pickerFor.rowId, patch);
    setPickerFor(null);
  };

  const total = rows.reduce((acc, r) => acc + (perRowVolume(r) ?? 0), 0);
  const anchorLabel = (() => {
    if (!pickerFor) return "";
    if (pickerFor.kind === "tubular") return isDrillstring ? "Component ID" : "Tubular OD";
    const r = rows.find((x) => x.id === pickerFor.rowId);
    return r && r.boundaryType === "openhole" ? "Hole Diameter" : "Casing ID";
  })();

  // -----------------------------------------------------------------------
  // Render — Drillstring mode (no boundary toggle, no outer column)
  // -----------------------------------------------------------------------
  if (isDrillstring) {
    return (
      <div data-testid="section-table" data-mode="drillstring" className="flex flex-col gap-3">
        <div className="overflow-x-auto rounded-md" style={{ border: "1px solid var(--rs-border)" }}>
          <table className="min-w-[640px] w-full text-[11.5px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.12em] text-[var(--rs-text-3)]">
                <th className="text-left p-2 font-semibold">#</th>
                <th className="text-left p-2 font-semibold">Component Type</th>
                <th className="text-left p-2 font-semibold">ID (in)</th>
                <th className="text-left p-2 font-semibold">Length (ft)</th>
                <th className="text-left p-2 font-semibold">Vol (bbl)</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const v = perRowVolume(r);
                return (
                  <tr
                    key={r.id}
                    data-testid={`section-row-${idx}`}
                    className="border-t"
                    style={{ borderColor: "rgba(148,163,184,0.18)" }}
                  >
                    <td className="p-2 font-mono-rs text-[var(--rs-text-3)] align-top">{idx + 1}</td>
                    <td className="p-2 align-top">
                      <input
                        data-testid={`section-row-${idx}-componentType`}
                        className="input-rs text-[12px] min-w-[140px]"
                        style={{ height: MIN_TOUCH }}
                        type="text"
                        placeholder="drill_pipe / hwdp / drill_collar / tubing"
                        value={r.componentType}
                        onChange={(e) => updateRow(r.id, { componentType: e.target.value })}
                      />
                    </td>
                    <td className="p-2 align-top">
                      <div className="flex flex-col gap-1 min-w-[140px]">
                        <input
                          data-testid={`section-row-${idx}-componentId`}
                          className="input-rs text-[12px]"
                          style={{ height: MIN_TOUCH }}
                          type="number"
                          step="0.001"
                          value={r.componentId}
                          placeholder="e.g. 4.276"
                          onChange={(e) => updateRow(r.id, { componentId: e.target.value, componentFromLib: false })}
                        />
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            data-testid={`section-row-${idx}-pick-tubular`}
                            onClick={() => openPicker(r.id, "tubular")}
                            className="btn-ghost rounded text-[10px] label-up px-2 inline-flex items-center gap-1"
                            style={{ minHeight: MIN_TOUCH * 0.7 }}
                          >
                            <BookOpen className="w-3 h-3" /> Pick
                          </button>
                          {r.componentFromLib ? (
                            <span className="text-[9px] tracking-wider uppercase text-amber-400 font-mono-rs">From library</span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="p-2 align-top">
                      <input
                        data-testid={`section-row-${idx}-length`}
                        className="input-rs text-[12px] min-w-[100px]"
                        style={{ height: MIN_TOUCH }}
                        type="number"
                        step="1"
                        value={r.length}
                        placeholder="e.g. 9000"
                        onChange={(e) => updateRow(r.id, { length: e.target.value })}
                      />
                    </td>
                    <td className="p-2 align-top font-mono-rs">
                      {v == null ? <span className="text-[var(--rs-text-3)]">—</span> : v.toFixed(2)}
                    </td>
                    <td className="p-2 align-top text-right">
                      <button
                        type="button"
                        data-testid={`section-row-${idx}-remove`}
                        onClick={() => removeRow(r.id)}
                        disabled={rows.length <= 1}
                        aria-label="Remove component"
                        className="inline-flex items-center justify-center rounded text-[var(--rs-text-3)] hover:text-[#FCA5A5] disabled:opacity-25 disabled:cursor-not-allowed"
                        style={{ width: MIN_TOUCH, height: MIN_TOUCH }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t" style={{ borderColor: "var(--rs-border)" }}>
                <td colSpan={4} className="p-2 text-right text-[var(--rs-text-3)] uppercase tracking-wider text-[10px] font-semibold">Total drill string internal volume</td>
                <td className="p-2 font-mono-rs font-bold text-amber-400" data-testid="section-table-total">{total.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <button
          type="button"
          data-testid="section-table-add"
          onClick={addRow}
          className="btn-ghost rounded-md inline-flex items-center justify-center gap-2 text-xs label-up self-start"
          style={{ minHeight: MIN_TOUCH, padding: "0 14px" }}
        >
          <Plus className="w-4 h-4" /> Add Component
        </button>

        <LibraryPicker
          open={!!pickerFor}
          onClose={() => setPickerFor(null)}
          libraryKinds={pickerKinds}
          anchorLabel={anchorLabel}
          onPick={handlePick}
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render — Annular mode (default, unchanged behavior)
  // -----------------------------------------------------------------------
  return (
    <div data-testid="section-table" data-mode="annular" className="flex flex-col gap-3">
      <div className="overflow-x-auto rounded-md" style={{ border: "1px solid var(--rs-border)" }}>
        <table className="min-w-[760px] w-full text-[11.5px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.12em] text-[var(--rs-text-3)]">
              <th className="text-left p-2 font-semibold">#</th>
              <th className="text-left p-2 font-semibold">Boundary</th>
              <th className="text-left p-2 font-semibold">Hole / Casing ID (in)</th>
              <th className="text-left p-2 font-semibold">Tubular Type</th>
              <th className="text-left p-2 font-semibold">Tubular OD (in)</th>
              <th className="text-left p-2 font-semibold">Length (ft)</th>
              <th className="text-left p-2 font-semibold">Vol (bbl)</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const v = perRowVolume(r);
              return (
                <tr
                  key={r.id}
                  data-testid={`section-row-${idx}`}
                  className="border-t"
                  style={{ borderColor: "rgba(148,163,184,0.18)" }}
                >
                  <td className="p-2 font-mono-rs text-[var(--rs-text-3)] align-top">{idx + 1}</td>
                  <td className="p-2 align-top">
                    <div className="flex gap-1">
                      {["casing", "openhole"].map((bt) => (
                        <button
                          key={bt}
                          type="button"
                          data-testid={`section-row-${idx}-boundary-${bt}`}
                          onClick={() => updateRow(r.id, { boundaryType: bt, outerFromLib: false })}
                          data-active={r.boundaryType === bt}
                          className="unit-pill text-[10px] px-2"
                          style={{ minHeight: MIN_TOUCH / 1.2 }}
                        >
                          {bt === "openhole" ? "Open Hole" : "Casing"}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    <div className="flex flex-col gap-1 min-w-[140px]">
                      <input
                        data-testid={`section-row-${idx}-outerId`}
                        className="input-rs text-[12px]"
                        style={{ height: MIN_TOUCH }}
                        type="number"
                        step="0.001"
                        value={r.outerId}
                        placeholder={r.boundaryType === "openhole" ? "e.g. 8.5" : "e.g. 12.415"}
                        onChange={(e) => updateRow(r.id, { outerId: e.target.value, outerFromLib: false })}
                      />
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          data-testid={`section-row-${idx}-pick-outer`}
                          onClick={() => openPicker(r.id, "outer")}
                          className="btn-ghost rounded text-[10px] label-up px-2 inline-flex items-center gap-1"
                          style={{ minHeight: MIN_TOUCH * 0.7 }}
                        >
                          <BookOpen className="w-3 h-3" /> Pick
                        </button>
                        {r.outerFromLib ? (
                          <span className="text-[9px] tracking-wider uppercase text-amber-400 font-mono-rs">From library</span>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    <input
                      data-testid={`section-row-${idx}-tubularType`}
                      className="input-rs text-[12px] min-w-[120px]"
                      style={{ height: MIN_TOUCH }}
                      type="text"
                      placeholder="drill_pipe / hwdp / dc"
                      value={r.tubularType}
                      onChange={(e) => updateRow(r.id, { tubularType: e.target.value })}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <div className="flex flex-col gap-1 min-w-[140px]">
                      <input
                        data-testid={`section-row-${idx}-tubularOd`}
                        className="input-rs text-[12px]"
                        style={{ height: MIN_TOUCH }}
                        type="number"
                        step="0.001"
                        value={r.tubularOd}
                        placeholder="e.g. 5"
                        onChange={(e) => updateRow(r.id, { tubularOd: e.target.value, tubularFromLib: false })}
                      />
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          data-testid={`section-row-${idx}-pick-tubular`}
                          onClick={() => openPicker(r.id, "tubular")}
                          className="btn-ghost rounded text-[10px] label-up px-2 inline-flex items-center gap-1"
                          style={{ minHeight: MIN_TOUCH * 0.7 }}
                        >
                          <BookOpen className="w-3 h-3" /> Pick
                        </button>
                        {r.tubularFromLib ? (
                          <span className="text-[9px] tracking-wider uppercase text-amber-400 font-mono-rs">From library</span>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    <input
                      data-testid={`section-row-${idx}-length`}
                      className="input-rs text-[12px] min-w-[100px]"
                      style={{ height: MIN_TOUCH }}
                      type="number"
                      step="1"
                      value={r.length}
                      placeholder="e.g. 1500"
                      onChange={(e) => updateRow(r.id, { length: e.target.value })}
                    />
                  </td>
                  <td className="p-2 align-top font-mono-rs">
                    {v == null ? <span className="text-[var(--rs-text-3)]">—</span> : v.toFixed(2)}
                  </td>
                  <td className="p-2 align-top text-right">
                    <button
                      type="button"
                      data-testid={`section-row-${idx}-remove`}
                      onClick={() => removeRow(r.id)}
                      disabled={rows.length <= 1}
                      aria-label="Remove section"
                      className="inline-flex items-center justify-center rounded text-[var(--rs-text-3)] hover:text-[#FCA5A5] disabled:opacity-25 disabled:cursor-not-allowed"
                      style={{ width: MIN_TOUCH, height: MIN_TOUCH }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t" style={{ borderColor: "var(--rs-border)" }}>
              <td colSpan={6} className="p-2 text-right text-[var(--rs-text-3)] uppercase tracking-wider text-[10px] font-semibold">Total annular volume</td>
              <td className="p-2 font-mono-rs font-bold text-amber-400" data-testid="section-table-total">{total.toFixed(2)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <button
        type="button"
        data-testid="section-table-add"
        onClick={addRow}
        className="btn-ghost rounded-md inline-flex items-center justify-center gap-2 text-xs label-up self-start"
        style={{ minHeight: MIN_TOUCH, padding: "0 14px" }}
      >
        <Plus className="w-4 h-4" /> Add Section
      </button>

      <LibraryPicker
        open={!!pickerFor}
        onClose={() => setPickerFor(null)}
        libraryKinds={pickerKinds}
        anchorLabel={anchorLabel}
        onPick={handlePick}
      />
    </div>
  );
}
